/* global $:true plyr:true */
/*
Store Locator Map Behaviour

This behaviour plugin controls the display of the map, its smarkers and the popover detail for the markers.

Markers can be defined in two ways:
1. through this components `addMarker` method, supplying lat, lng, icon and html (for the popover)
2. populating similar objects into the `data` option of this component:
    ```
    data-store-locator-map-data="[
        {lat: "", lng: "", icon: "", html: ""},
    ]"
    ```

If popover markup is present here in this component, then details are displayed when a marker is clicked.

Further reading: https://developers.google.com/maps/documentation/javascript/reference/3.exp/


Styleguide: STR001.Components.StoreLocatorMapBehaviour
*/

import debug from "debug";
import toast from '@vendor/pyrsmk-toast/toast';

import { ELEMENT_SELECTOR, MODULE_NAME } from './index';
import googleMapsApiPromise from '../../services/google-maps-api';
import { collectElementOptions } from '../../lib/objects';
import debounce from '../../lib/debounce';

const log = debug(MODULE_NAME);

// https://github.com/appleple/modal-video
export class StoreLocatorMap {

    constructor(element, options) {
        this.element = element;
        this.$element = $(element);
        this.options = options;
        log('construct', this.element, this.options);

        this.data = this.options.markers || [];
        this.markers = [];
        this.queues = {
            componentReady: [],
            mapCreated: [],
            markersPlaced: []
        };

        this.$viewportElement = this.$element.find(this.options.viewportSelector);
        this.$maskElement = this.$element.find(this.options.maskSelector);
        this.$popoverElement = this.$element.find(this.options.popoverSelector);
        this.$popoverContentElement = this.$popoverElement.find(this.options.popoverContentSelector);
        this.$popoverCloseButton = this.$popoverElement.find(this.options.popoverCloseSelector);
        this.$popoverCloseButton.on('click', this.closePopover.bind(this));

        this.closePopover();

        this.init();
        this.flush('componentReady');

    }

    init () {
        log('init');

        googleMapsApiPromise.then((google) => {
            this.google = google;

            toast(
                `https://cdn.jsdelivr.net/gh/googlemaps/v3-utility-library@141697fa36c9a4577bb093ba6ea4cb0d9453600c/markerwithlabel/src/markerwithlabel_packed.js`,
                this.options.cluster && `https://cdn.jsdelivr.net/gh/googlemaps/v3-utility-library@141697fa36c9a4577bb093ba6ea4cb0d9453600c/markerclustererplus/src/markerclusterer_packed.js`,
                () => {
                    this.createMap();
                    this.flush('mapCreated');
                    this.placeMarkers();
                    this.flush('markersPlaced');
                });
        });

        this.on('markersPlaced', debounce(() => {
            if (this.options.useBrowserGps) {
                this.gpsLocation
                    .then((position) => {
                        const { latitude, longitude } = position.coords;
                        this.options.lat = latitude;
                        this.options.lng = longitude;
                        const center = new this.google.maps.LatLng(latitude, longitude);
                        this.moveMapTo(center);
                        const nearestMarker = this.getNearestMarker();
                        this.panToFitPosition(nearestMarker.position);

                        if (this.options.showDirections) {
                            this.showDirections(center, nearestMarker.position);
                        }


                    })
                    .catch((err) => {
                        const { message} = err;
                        log(`init.geolocationErr: ${message}`);
                    });
            }
        }, 150));

        this.$element.on('directions', this.showDirections.bind(this));

        $(window).on('resize', debounce(() => {
            if (this.selectedMarker && this.popoverIsOpen) {
                log('window.resize', this.selectedMarker);
                this.pointPopoverAtMarker(this.selectedMarker.latLng);
            }
        }, 100));

        this.$maskElement.on('click', () => {
            if (this.popoverIsOpen) {
                this.closePopover();
            }
        });


    }

    on (queueName, fn) {
        if (!this.queues[queueName]) {
            this.queues[queueName] = [];
        }
        this.queues[queueName].push(fn);
    }

    flush (queueName) {
        const queue = this.queues[queueName];
        if (queue && queue.length) {
            while (queue.length) {
                const task = queue.pop();
                if (task && typeof task == 'function') task();
            }
        }
    }

    get gpsLocation () {
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(resolve, reject);
            } else {
                reject(new Error('navigator.geolocation not available'));
            }
        });
    }

    get mapOptions () {
        return new Promise((resolve, reject) => {
            const {
				lat, lng,
                zoom,
                gestureHandling,
                zoomcontrol,
                draggable,
                clickableIcons,
                styles,
                minZoom,
                maxZoom,
                tilt,
                backgroundColor,
                disableDefaultUI,
                disableDoubleClickZoom,
                draggableCursor,
                draggingCursor,
                fullscreenControl,
                fullscreenControlOptions,
                heading,
                keyboardShortcuts,
                mapTypeControl,
                mapTypeControlOptions,
                mapTypeId,
                noClear,
                panControl,
                panControlOptions,
                rotateControl,
                rotateControlOptions,
                scaleControl,
                scaleControlOptions,
                scrollwheel,
                streetViewControl,
                zoomControl,
				zoomControlOptions
            } = this.options;

            const options = {
				center: { lat, lng },
                zoom,
                minZoom,
                maxZoom,
                tilt,
                gestureHandling,
                draggable,
                clickableIcons,
                styles,
                backgroundColor,
                disableDefaultUI,
                disableDoubleClickZoom,
                draggableCursor,
                draggingCursor,
                fullscreenControl,
                fullscreenControlOptions,
                heading,
                keyboardShortcuts,
                mapTypeControl,
                mapTypeControlOptions,
                mapTypeId,
                noClear,
                panControl,
                panControlOptions,
                rotateControl,
                rotateControlOptions,
                scaleControl,
                scaleControlOptions,
                scrollwheel,
                streetViewControl,
                zoomControl,
				zoomControlOptions
            };

            resolve(options);

        });

    }

    createMap () {
        if (!this.map) {
            this.mapOptions.then(mapOptions => {
                log('createMap', mapOptions);
                this.map = new this.google.maps.Map(this.$viewportElement.get(0), mapOptions);

                if (this.options.cluster) {
                    // http://htmlpreview.github.io/?https://github.com/googlemaps/v3-utility-library/blob/master/markerclustererplus/docs/reference.html
                    this.clusterer = new MarkerClusterer(this.map, this.data, {
                        gridSize: this.options.gridSize,
                        maxZoom: this.options.maxZoom,
                        averageCenter: this.options.clusterAverageCenter,
						clusterClass: this.options.clusterClass,
						clusterDisplayCount: this.options.clusterDisplayCount,

                        calculator: (markers, styles) => {
							console.log('this.options.clusterDisplayCount', this.options.clusterDisplayCount);
                            return {
                                text: this.options.clusterDisplayCount ? `<span class="${this.options.clusterLabelClass}">${markers.length}</span>` : '',
                                index: markers.length
                            };
						}
                    });

                    // without this, averageCenter doesn't work properly.
                    this.getImageDimensions(this.options.defaultClusterIcon)
                        .then(({ width, height}) => {
                            this.clusterer.setStyles([
                                {
                                    url: this.options.defaultClusterIcon,
                                    width,
                                    height,
                                }
                            ]);
                        });

                    this.flush('clustererReady');

				}

                log('createMap.done');
            });

        } else {
            // Destroying and recreating google map instances is not offically supported.
            //  - https://issuetracker.google.com/issues/35821412#comment32
            // But, there is community provided method to do this.
            //  - https://issuetracker.google.com/issues/35821412#comment53
            // I didn't implement it because it seems ecssive and not required for this use cause.
            log('createMap.alreadyExists');
        }
    }

    getImageDimensions (image) {
        return new Promise((resolve, reject) => {
            const imageElement = new Image();
            imageElement.onload = () => {
                resolve({
                    width: imageElement.width,
                    height: imageElement.height
                });
            };
            imageElement.onerror = (err) => {
                reject(`Error loading markerIcon ${image}`, {err});
            };
            imageElement.src = image;
        });
    }

    addMarker({ lat, lng, icon = this.options.defaultMarkerIcon, label, html}) {
        log('addMarker');
        return this.getImageDimensions(icon)
            .catch(log)
            .then((dimensions) => {
                const markerOptions = {
                    position: { lat, lng },
                    icon,
                    anchorPoint: new this.google.maps.Point(dimensions.width / 2, 0),
                    labelContent: label.toString(),
                    labelClass: this.options.markerLabelClass,
                };

                if (!this.options.cluster) markerOptions.map = this.map;

                const marker = new MarkerWithLabel(markerOptions);
                marker.html = html;

                marker.addListener('click', () => {
                    if (!marker.html) return;
                    this.panAndOpenMarkerPopover(marker);
                });

                this.markers.push(marker);

                if (this.options.cluster) {
                    this.clusterer.addMarker(marker);
                };

                return marker;
            });
    }

    showDirections(from, to, mode = google.maps.TravelMode.WALKING) {
        if (!this.google.maps.DirectionsService || !this.google.maps.DirectionsRenderer) return;

        const directionsService = new this.google.maps.DirectionsService();
        const directionsDisplay = new this.google.maps.DirectionsRenderer();

        directionsDisplay.setMap(this.map);
        directionsService.route({
            origin: from,
            destination: to,
            travelMode: mode
        }, (response, status) => {
            if (status == this.google.maps.DirectionsStatus.OK) {
                directionsDisplay.setDirections(response);
                directionsDisplay.setOptions({
                    suppressMarkers: true,
                    preserveViewport: false
                });

                // this.panToFitPosition(to);

                log('directions.set', response);
            }
        });
    }

    placeMarkers () {
        log('placeMarkers');
        this.data.forEach((location) => {
            this.addMarker(location);
        });
        this.flush('markersPlaced');

    }

    popoverContent ({html}) {
        this.$popoverContentElement.html(html);
    }
    get popoverIsOpen () {
        return this.$element.hasClass(this.options.popoverOpenClass);
    }
    closePopover () {
        this.$element.removeClass(this.options.popoverOpenClass);
    }

    openPopover () {
        this.$element.addClass(this.options.popoverOpenClass);
    }
    panAndOpenMarkerPopover (marker) {
        this.selectedMarker = marker;
        this.popoverContent({html: marker.html});
        this.openPopover();
        this.offsetCenterPercentage(
            marker.position,
            this.options.mapCenterOffsetX,
            this.options.mapCenterOffsetY);
        this.pointPopoverAtMarker(marker.position);
    }

    offsetCenter (latlng, offsetx, offsety) {

        // latlng is the apparent centre-point
        // offsetx is the distance you want that point to move to the right, in pixels
        // offsety is the distance you want that point to move upwards, in pixels
        // offset can be negative
        // offsetx and offsety are both optional

        var scale = Math.pow(2, this.map.getZoom());

        var worldCoordinateCenter = this.map.getProjection().fromLatLngToPoint(latlng);
        var pixelOffset = new this.google.maps.Point((offsetx / scale) || 0, (offsety / scale) || 0);

        var worldCoordinateNewCenter = new this.google.maps.Point(
            worldCoordinateCenter.x - pixelOffset.x,
            worldCoordinateCenter.y + pixelOffset.y
        );

        var newCenter = this.map.getProjection().fromPointToLatLng(worldCoordinateNewCenter);

        this.map.setCenter(newCenter);

    }

    offsetCenterPercentage(center, offsetX = 0.25, offsetY = 0.25) {
        const span = this.map.getBounds().toSpan(); // a latLng - # of deg map spans

        const newCenter = {
            lat: center.lat() + span.lat() * offsetY,
            lng: center.lng() + span.lng() * offsetX
        };

        this.map.setCenter(newCenter);  // or map.setCenter(newCenter);
    }

    latLngToPixels (latlng) {
        const projection = this.map.getProjection();
        const bounds = this.map.getBounds();
        const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
        const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
        const scale = Math.pow(2, this.map.getZoom());
        const worldPoint = projection.fromLatLngToPoint(latlng);
        return {
            x: Math.floor((worldPoint.x - bottomLeft.x) * scale),
            y: Math.floor((worldPoint.y - topRight.y) * scale)
        };
    }

    pixelsToLatLng ({x, y}) {
        // retrieve the lat lng for the far extremities of the (visible) map
        const latLngBounds = this.map.getBounds();
        const neBound = latLngBounds.getNorthEast();
        const swBound = latLngBounds.getSouthWest();

        // convert the bounds in pixels
        const neBoundInPx = this.map.getProjection().fromLatLngToPoint(neBound);
        const swBoundInPx = this.map.getProjection().fromLatLngToPoint(swBound);

        // compute the percent of x and y coordinates related to the div containing the map; in my case the screen
        const procX = x / this.$viewportElement.width();
        const procY = y / this.$viewportElement.height();

        // compute new coordinates in pixels for lat and lng;
        // for lng : subtract from the right edge of the container the left edge,
        // multiply it by the percentage where the x coordinate was on the screen
        // related to the container in which the map is placed and add back the left boundary
        // you should now have the Lng coordinate in pixels
        // do the same for lat
        const newLngInPx = (neBoundInPx.x - swBoundInPx.x) * procX + swBoundInPx.x;
        const newLatInPx = (swBoundInPx.y - neBoundInPx.y) * procY + neBoundInPx.y;

        // convert from google point in lat lng and have fun :)
        return this.map.getProjection().fromPointToLatLng(new this.google.maps.Point(newLngInPx, newLatInPx));

    }

    pointPopoverAtMarker (point) {
        const {x, y} = this.latLngToPixels(point);

        this.$popoverElement.css({
            left: x,
            top: y - this.$popoverElement.height()
        });
    }

    moveMapTo(point, zoom = this.options.zoom) {
        this.map.setCenter(point);
        this.map.setZoom(zoom);
    }

    getNearestMarker () {
        const marker = this.markers.map(marker => {
                marker.distance = this.google.maps.geometry.spherical
                    .computeDistanceBetween(marker.position, this.map.getCenter());
                return marker;
            })
            .sort((current, next) => {
                return current.distance > next.distance ? 1 : -1;
            })
            .slice(0, 1)[0];
        return marker;
    }

    panToFitPosition (position) {
        if (!position) return;
        const bounds = new this.google.maps.LatLngBounds();
        bounds.extend(this.map.getCenter());
        this.map.fitBounds(bounds);
    }

    panToFitMarkers (markers) {
        if (!markers) return;
        if (markers && markers.length) {
            const bounds = markers.reduce((bounds, marker) => {
                if (!bounds.contains(marker.position)) bounds.extend(marker.position);
                return bounds;
            }, new this.google.maps.LatLngBounds());

            if (bounds) {
                bounds.extend(this.map.getCenter());
                this.map.fitBounds(bounds);
            }
        }
    }

}

StoreLocatorMap.DEFAULTS = {
    lat: 0,
    lng: 0,
    zoom: 8,
    tilt: 0, // 0 or 45
    draggable: true,
    gestureHandling: null,
    maxZoom: 15,
    minZoom: 1,
    gridSize: 50,
    // reference: https://developers.google.com/maps/documentation/javascript/style-reference
    // wizard tool: https://mapstyle.withgoogle.com/
    styles: null,

    mapCenterOffsetX: 0,
    mapCenterOffsetY: 0.25,
    defaultMarkerIcon: null,

    cluster: true,
    defaultClusterIcon: null,
    clusterAverageCenter: true,
    clusterClass: 'store-locator-map__cluster',
    clusterLabelClass: 'store-locator-map__cluster-label',

    showDirections: false,
    useBrowserGps: false,
    minimumVisibleMarkers: 1,
    markerLabelClass: 'store-locator-map__marker-label',
    mapEdgePaddingPx: 32,
    viewportSelector: '.store-locator-map__viewport',
    maskSelector: '.store-locator-map__mask',
    popoverSelector: '.store-locator-map__popover',
    popoverCloseSelector: '.store-locator-map__popover-close-button',
    popoverContentSelector: '.store-locator-map__popover-content',
    popoverOpenClass: 'store-locator-map--popover-open',
    streetView: false,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    rotateControl: false,
    scaleControl:false,
    disableDefaultUI: false
};



// WEBPACK FOOTER //
// ./project/client/src/core/modules/store-locator-map/component.js